import Client from '@/commonFunc/client.js'
import docsRoutes from '@/router/docs.js'
import BaseConfig from '@/config/base.js'
import { resolveLandingExternalSource } from '@/utils/ta-landing.js'

/** 官网仅统计 P3_Page 相关路由 */
const OFFICIAL_WEBSITE_PATHS = new Set(docsRoutes.map((r) => r.path))

/** 顺序即优先级：先匹配客户端 H5，再匹配官网 */
const TA_PAGE_VIEW_STRATEGIES = [
  {
    match: (ctx) => ctx.hasLogin,
    eventName: 'page_view_h5',
  },
  {
    match: (ctx) => !ctx.hasLogin && OFFICIAL_WEBSITE_PATHS.has(ctx.to.path),
    eventName: 'page_view_official_website',
  },
]

// 根据登录态与目标路由解析本次 PV 事件名：登录 → page_view_h5；未登录且命中官网路由 → page_view_official_website；否则不上报
function resolveTaPageViewEventName(to, from) {
  // prettier-ignore
  try {
    const { uid, token } = Client.getUserBaseInfo()
    const ctx = { to, from, hasLogin: !!(uid && token) }
    const hit = TA_PAGE_VIEW_STRATEGIES.find((s) => s.match(ctx))
    return hit ? hit.eventName : null
  }
  catch (e) {
    console.log('resolveTaPageViewEventName', e)
    return null
  }
}

// SDK 未就绪时暂存 PV 事件的内存队列
const queue = []

// 上报 PV：SDK 就绪立即 track，未就绪入队等待 flush
export function trackTaPageView(eventName, props) {
  if (!BaseConfig.isRelease) return
  // prettier-ignore
  try {
    // 已初始化
    if (window.ta) {
      window.ta.track(eventName, props)
    }
    // 未初始化放在队列
    else {
      queue.push({ eventName, props })
    }
  }
  catch (e) {
    console.log('trackTaPageView', e)
  }
}

// SDK init 完成后调用：把队列里暂存的 PV 事件依次补发并清空
export function flushTaPageViewQueue() {
  if (!window.ta || !queue.length) return
  while (queue.length) {
    const { eventName, props } = queue.shift()
    // prettier-ignore
    try {
      window.ta.track(eventName, props)
    }
    catch (e) {
      console.log('flushTaPageViewQueue', e)
    }
  }
}

// 构建当前页 page_url：按路由模式拼 host + path，无路由时回退用 location.href 去查询串
function buildPageUrl(to, isHistoryMode) {
  // prettier-ignore
  try {
    if (to && to.path) {
      const path = to.fullPath || to.path
      return isHistoryMode ? `${window.location.host}${path}` : `${window.location.host}/#${path}`
    }
    const href = window.location.href
    const queryIndex = href.indexOf('?')
    return queryIndex === -1 ? href : href.slice(0, queryIndex)
  }
  catch (e) {
    console.log('buildPageUrl', e)
    return 'unknown'
  }
}

// 判断 from 是否站内导航（matched 非空），用于区分 page_from 取站内路径还是站外来源
function isIntrasiteNavigation(from) {
  return from && Array.isArray(from.matched) && from.matched.length > 0
}

// 构建上一页 page_from：站内导航取 from 路由路径，站外进入取 ta-landing 解析的来源
function buildPageFrom(from, isHistoryMode) {
  // prettier-ignore
  try {
    if (isIntrasiteNavigation(from)) {
      const p = from.fullPath || from.path || '/'
      return isHistoryMode ? `${window.location.host}${p}` : `${window.location.host}/#${p}`
    }
    return resolveLandingExternalSource()
  }
  catch (e) {
    console.log('buildPageFrom', e)
    return 'unknown'
  }
}

// 组装 PV 上报属性：page_url + page_from
function buildPayload(to, from, isHistoryMode) {
  // prettier-ignore
  try {
    return {
      page_url: buildPageUrl(to, isHistoryMode),
      page_from: buildPageFrom(from, isHistoryMode),
    }
  }
  catch (e) {
    console.log('buildPayload', e)
    return {
      page_url: 'unknown',
      page_from: 'unknown',
    }
  }
}

// 注册路由 afterEach 钩子：每次跳转按策略上报对应 PV 事件
export function registerTaRoutePageView(router, isHistoryMode = false) {
  router.afterEach((to, from) => {
    if (!BaseConfig.isRelease) return
    // prettier-ignore
    try {
      const eventName = resolveTaPageViewEventName(to, from)
      if (!eventName) return
      trackTaPageView(eventName, buildPayload(to, from, isHistoryMode))
    }
    catch (e) {
      console.log('registerTaRoutePageView afterEach', e)
    }
  })
}
